
import { Component, Injector, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { filter, distinctUntilChanged, tap } from 'rxjs/operators';
import { BaseRoute } from '../../shared/components/base.route';
import { QuestionnaireService } from '../../shared/services/questionnaire.service';
import { TKAStrategyType } from '../../shared/models/SelectOptions/TKAStrategyType';
import * as _ from 'lodash';

@Component({
  selector: 'md-make-changes-modal',
  template: require('./make-changes-modal.component.html'),
  styles: [require('./make-changes-modal.component.scss')]
})

export class MakeChangesModalComponent extends BaseRoute {
  @ViewChild('form') formElement;
  @Input() formData;
  @Output() closeEmitter = new EventEmitter();
  originalGoalSession = {};
  formIsValid = false;
  selectedCashAllocation;

  // The user is always shown a proper name of Tax Optimized or Income
  // What is actually stored depends on the user's cash vested status
  // We just check for both options
  strategyOptionMap = new Map([
    ['Tax Optimized', ['TaxOptimized', 'CashEnhancedTaxOptimized']],
    ['Income', ['Income', 'CashEnhancedIncome']]
  ])

  constructor(injector: Injector,
    questionnaire: QuestionnaireService) {
    super(injector);
    this.questionnaire = questionnaire;
    this.strategy = this.appActions.getData('goalSession.strategy');
    this.strategies = TKAStrategyType;
  }

  ngOnInit() {
    // Save a copy of goal so
    // if the user does not update their recommendation then revert to original goal
    this.originalGoalSession = { ...this.flowManager.goalSession };
    this.selectedCashAllocation = this.originalGoalSession.cashAllocation;
    this.form = this.formData;
  }

  updateOptionValue(event) {
    this.strategy = this.appActions.getData('goalSession.strategy');
    if (event.id === 'cashAllocation' && event.showHide) {
      if (event.showHide.id !== this.selectedCashAllocation) {

        this.selectedCashAllocation = event.showHide.id;

        if (this.selectedCashAllocation === 'cashEnhanced') {

          this.appActions.setData('goalSession.isCashVested', true);
        } else {
          this.appActions.setData('goalSession.isCashVested', false);
        }
        this.filterStrategies();
        this.appActions.setData('goalSession.strategy', this.questionnaire.toggleStrategy(this.strategy));
        this.strategy = this.appActions.getData('goalSession.strategy');
      }
    }
  }

  ngAfterViewInit() {
    // Select Options Service (ln86) is messing this up
    // They use name to filter for an option which isn't unique for our list
    // Switching to 'code' property would fix but then create problems elsewhere
    // Easiest fix...
    this.flowManager.goalSession = { ...this.originalGoalSession };
    // Narrow down strategy list options to cashVested or non cashVested
    this.form.inputs.strategy.options = this.form.inputs.strategy.options.filter(
      x => x.isCashVested === this.flowManager.goalSession.isCashVested);

    // Filter down the strategy options as soon as they're available
    this.filterStrategies();

    // TODO: Update retirement to account type
    // Each time the user updates their goal or account type
    // then update the available strategy options
    this.formElement.valueChange.pipe(
      distinctUntilChanged(),
      filter(x => x.id === 'goal' || x.id === 'retirement'),
      tap(x => this.filterStrategies()))
      .subscribe(() => {});
  }

  ngOnDestroy() {
    if (!this.formIsValid) {
      this.resetGoalSession();
    }
  }

  btnClick(e) {
    if (e.action === 'next') {
      // Check if goal session is different from original
      // If they're the same just close
      if (_.isEqual(this.originalGoalSession, this.flowManager.goalSession)) {
        this.formIsValid = true;
        this.closeEmitter.emit();
        return;
      }
      // Validate the form
      // If no items exist in the invalidFields array then we can assume
      // the form is valid
      this.validation.validateForm();
      if (this.formElement && this.formElement.invalidFields.length === 0) {
        this.formIsValid = true;
        // Mark goal session as dirty so a recommendation will be made
        this.flowManager.goalSession = { ...this.flowManager.goalSession, isDirty: true };
        this.questionnaire.updatePortfolio();
        this.closeEmitter.emit();
        return;
      }
    } else {
      this.closeEmitter.emit();
      return;
    }
  }

  filterStrategies() {
    // Get current goal and account type
    // These two values influence what is available in portfolio type
    // TODO: rename retirement to account type
    const accountTaxType = this.flowManager.goalSession.accountTaxType;
    const goal = this.flowManager.goalSession.goal;

    this.resetStrategyOptions();
      // Only display Income portfolio when there is GoalType of 'Income'
    if (goal !== 'income') {
      this.removeStrategyOption('Income');
    }
      // Only display Tax-Optimized when there is an AccountTaxType of 'Taxable'
    if (accountTaxType !== 'taxable') {
      this.removeStrategyOption('Tax Optimized');
    }
  }

  // The strategies that are available to be selected need to
  // be reset each time a change is made so the list can be
  // refiltered down to the appropriate options
  resetStrategyOptions() {
    this.form.inputs.strategy.options.length = 0;
    this.form.inputs.strategy.options = this.strategies.filter(obj =>
      obj.isCashVested === this.appActions.getData('goalSession.isCashVested'));
  }

  // Remove the strategy option and reset the user's strategy
  // if it is an option that has been removed
  removeStrategyOption(strategyOption) {
    // TaxOptimized: TaxOptimized, CashEnhancedTaxOptimized
    // Income: CashEnhancedIncome, Income
    // Name is used as the filter because they are displayed the same way to the user
    // Underneath it will either include the prefix CashEnhanced or not
    const index = this.form.inputs.strategy.options.findIndex(opt => opt.name === strategyOption);
    if (index !== -1) {
      this.form.inputs.strategy.options.splice(index, 1);
      if (this.strategyOptionMap.get(strategyOption).includes(this.flowManager.goalSession.strategy)) {
        this.flowManager.resetStrategy();
      }
    }
  }

  // Reset Goal Session to what it was originally
  // This is called if user closes modal any other way that isn't through
  // update recommendation button click
  resetGoalSession() {
    this.flowManager.goalSession = this.originalGoalSession;
  }
}
